
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdminStats } from '@/hooks/useAdminStats';
import { Users, DollarSign, CreditCard, TrendingUp, UserPlus, UserX } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const AdminDashboard = () => {
  const { data: stats, isLoading, error } = useAdminStats();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard Administrativo</h1>
          <p className="text-muted-foreground">Visão geral da plataforma</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-16" />
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive/20 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">Erro ao Carregar Dados</CardTitle>
            <CardDescription className="text-destructive/80">
              Não foi possível carregar as estatísticas. Tente novamente.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const { userStats, revenueStats } = stats || {};

  const dashboardCards = [
    {
      title: 'Total Personal Trainers',
      value: userStats?.total_trainers ?? 0,
      icon: Users,
      description: `${userStats?.active_trainers ?? 0} ativos`,
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      title: 'Total Alunos',
      value: userStats?.total_students ?? 0,
      icon: UserPlus,
      description: `${userStats?.active_students ?? 0} ativos`,
      iconColor: 'text-green-600 dark:text-green-400'
    },
    {
      title: 'Subscrições Ativas',
      value: userStats?.active_subscriptions ?? 0,
      icon: CreditCard,
      description: `de ${userStats?.total_subscriptions ?? 0} totais`,
      iconColor: 'text-purple-600 dark:text-purple-400'
    },
    {
      title: 'Receita Mensal',
      value: `€${Number(revenueStats?.monthly_revenue ?? 0).toFixed(2)}`,
      icon: DollarSign,
      description: 'Receita atual',
      iconColor: 'text-orange-600 dark:text-orange-400'
    },
    {
      title: 'Receita Total',
      value: `€${Number(revenueStats?.total_revenue ?? 0).toFixed(2)}`,
      icon: TrendingUp,
      description: 'Todas as subscrições',
      iconColor: 'text-indigo-600 dark:text-indigo-400'
    },
    {
      title: 'Valor Médio Subscrição',
      value: `€${Number(revenueStats?.avg_subscription_value ?? 0).toFixed(2)}`,
      icon: UserX,
      description: 'Por utilizador ativo',
      iconColor: 'text-pink-600 dark:text-pink-400'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Dashboard Administrativo</h1>
        <p className="text-muted-foreground">Visão geral da plataforma TrainerFOLIO</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardCards.map((card, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <div className="p-2 rounded-full bg-secondary">
                <card.icon className={`h-4 w-4 ${card.iconColor}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                {card.value}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {card.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ações Rápidas</CardTitle>
            <CardDescription>
              Acesso rápido às funcionalidades mais utilizadas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between items-center p-3 bg-secondary rounded-lg">
              <span className="text-sm text-foreground">Gerir Utilizadores</span>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex justify-between items-center p-3 bg-secondary rounded-lg">
              <span className="text-sm text-foreground">Ver Subscrições</span>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex justify-between items-center p-3 bg-secondary rounded-lg">
              <span className="text-sm text-foreground">Relatórios Financeiros</span>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Atividade Recente</CardTitle>
            <CardDescription>
              Últimas atividades na plataforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-muted-foreground">Sistema funcionando normalmente</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-muted-foreground">Dados atualizados automaticamente</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-muted-foreground">Monitorização ativa</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
